import { observable, action, computed, runInAction } from 'mobx';
import util from 'preact-util';

class IdexxStore {
    @observable tests = [];
    @observable species = [];
    @observable breeds = [];
    @observable genders = [];
    @observable devices = [];
    @observable results = [];
    @observable orders = [];
    @observable unlinkedResults = [];
    @observable animalResults = [];
    @observable selectedResult = null;
    @observable selectedOrder = null;
    @observable loading = false;
    @observable error = null;
    @observable latestBatchId = null;

    // Per-species breed cache — keeps GET /breeds?speciesCode=X results
    // so the order form dropdown re-uses previously fetched lists.
    _breedsBySpecies = {};

    constructor() {
        this.name = 'idexx';
    }

    // ============================================
    // Cached reference-data loaders
    // ============================================
    // These return whatever is in the observables. They only hit the API
    // when the cache is empty. The order form calls these instead of
    // util.fetchApi directly so reopening the drawer is instant.

    @action
    async ensureSpecies() {
        if (this.species && this.species.length > 0) return this.species;
        await this.getSpecies();
        return this.species;
    }

    @action
    async ensureGenders() {
        if (this.genders && this.genders.length > 0) return this.genders;
        await this.getGenders();
        return this.genders;
    }

    @action
    async ensureTests() {
        if (this.tests && this.tests.length > 0) return this.tests;
        await this.getTests();
        return this.tests;
    }

    @action
    async ensureDevices() {
        if (this.devices && this.devices.length > 0) return this.devices;
        await this.getDevices();
        return this.devices;
    }

    @action
    async ensureBreeds(speciesCode) {
        if (!speciesCode) return [];
        const cached = this._breedsBySpecies[speciesCode];
        if (cached && cached.length > 0) {
            runInAction(() => { this.breeds = cached; });
            return cached;
        }
        await this.getBreeds(speciesCode);
        const list = Array.isArray(this.breeds) ? this.breeds : [];
        this._breedsBySpecies[speciesCode] = list;
        return list;
    }

    clearReferenceDataCache() {
        runInAction(() => {
            this.species = [];
            this.genders = [];
            this.tests = [];
            this.devices = [];
            this.breeds = [];
        });
        this._breedsBySpecies = {};
    }

    // ============================================
    // Credentials Management
    // ============================================

    @action
    async saveCredentials({ username, password, environment = 'production' }) {
        this.loading = true;
        this.error = null;

        try {
            // PIMS ID is server-wide config now — not sent from client.
            const response = await util.fetchApi('/api/integrations/idexx/save-credentials', {
                method: 'POST',
            }, {
                username,
                password,
                environment,
            });

            if (response.status === 200) {
                console.log('IDEXX credentials saved successfully');
                // Reload user info to get updated customer data
                const userStore = require('./user').default;
                await userStore.getInfo();
            }

            runInAction(() => {
                if (response.status !== 200) {
                    this.error = response.message || 'Failed to save credentials';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async revokeCredentials() {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/idexx/revoke', {
                method: 'POST',
            });

            if (response.status === 200) {
                console.log('IDEXX credentials revoked successfully');
                const userStore = require('./user').default;
                await userStore.getInfo();
            }

            runInAction(() => {
                if (response.status !== 200) {
                    this.error = response.message || 'Failed to revoke credentials';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async validateCredentials({ username, password, environment = 'production' }) {
        this.loading = true;
        this.error = null;

        try {
            // Save temporarily to test
            const response = await util.fetchApi('/api/integrations/idexx/save-credentials', {
                method: 'POST',
            }, {
                username,
                password,
                environment,
                testConnection: true,
            });

            runInAction(() => {
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            return { status: 500, message: error.message, data: { valid: false } };
        }
    }

    // ============================================
    // Reference Data (PIMS Ordering API)
    // ============================================

    @action
    async getTests(params = {}) {
        this.loading = true;
        this.error = null;

        try {
            const queryString = new URLSearchParams(params).toString();
            const url = `/api/integrations/idexx/tests${queryString ? '?' + queryString : ''}`;

            const response = await util.fetchApi(url);

            runInAction(() => {
                if (response.status === 200) {
                    this.tests = response.data || [];
                } else {
                    this.error = response.message || 'Failed to fetch tests';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async getSpecies() {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/idexx/species');

            runInAction(() => {
                if (response.status === 200) {
                    this.species = response.data || [];
                } else {
                    this.error = response.message || 'Failed to fetch species';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async getBreeds(speciesCode = null) {
        this.loading = true;
        this.error = null;

        try {
            const params = speciesCode ? { speciesCode } : {};
            const queryString = new URLSearchParams(params).toString();
            const url = `/api/integrations/idexx/breeds${queryString ? '?' + queryString : ''}`;

            const response = await util.fetchApi(url);

            runInAction(() => {
                if (response.status === 200) {
                    this.breeds = response.data || [];
                } else {
                    this.error = response.message || 'Failed to fetch breeds';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async getGenders() {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/idexx/genders');

            runInAction(() => {
                if (response.status === 200) {
                    this.genders = response.data || [];
                } else {
                    this.error = response.message || 'Failed to fetch genders';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async getDevices() {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/idexx/devices');

            runInAction(() => {
                if (response.status === 200) {
                    this.devices = response.data || [];
                } else {
                    this.error = response.message || 'Failed to fetch devices';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    // ============================================
    // Orders (PIMS Ordering API)
    // ============================================

    @action
    async createOrder({ tests, journalId, clinicalNotes, specimenCollectionDate, speciesCode, breedCode, genderCode, modality, ivlsSerialNumbers }) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/idexx/order', {
                method: 'POST',
            }, {
                tests,
                journalId,
                clinicalNotes,
                specimenCollectionDate,
                speciesCode,
                breedCode,
                genderCode,
                modality,
                ivlsSerialNumbers,
            });

            runInAction(() => {
                if (response.status === 200 && response.data?.order) {
                    // Add to local orders list
                    this.orders = [response.data.order, ...this.orders];
                    this.selectedOrder = response.data.order;
                } else {
                    this.error = response.message || 'Failed to create order';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async getOrder(orderId) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi(`/api/integrations/idexx/order/${orderId}`);

            runInAction(() => {
                if (response.status === 200) {
                    this.selectedOrder = response.data?.localOrder || response.data;
                } else {
                    this.error = response.message || 'Failed to fetch order';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async cancelOrder(orderId) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi(`/api/integrations/idexx/order/${orderId}`, {
                method: 'DELETE',
            });

            runInAction(() => {
                if (response.status === 200) {
                    // Update local orders list
                    this.orders = this.orders.map(o =>
                        o.id === orderId ? { ...o, status: 'cancelled' } : o
                    );
                    if (this.selectedOrder?.id === orderId) {
                        this.selectedOrder = { ...this.selectedOrder, status: 'cancelled' };
                    }
                } else {
                    this.error = response.message || 'Failed to cancel order';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    // ============================================
    // Results (PIMS Results API V3)
    // ============================================

    @action
    async getLatestResults() {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/idexx/results/latest');

            runInAction(() => {
                if (response.status === 200) {
                    this.results = response.data?.results || [];
                    this.latestBatchId = response.data?.batchId || null;
                } else {
                    this.error = response.message || 'Failed to fetch results';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async confirmResults(batchId) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi(`/api/integrations/idexx/results/confirm/${batchId}`, {
                method: 'POST',
            });

            runInAction(() => {
                if (response.status === 200) {
                    // Clear batch ID after confirmation
                    if (this.latestBatchId === batchId) {
                        this.latestBatchId = null;
                    }
                } else {
                    this.error = response.message || 'Failed to confirm batch';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async getResultDetails(resultId) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi(`/api/integrations/idexx/results/${resultId}`);

            runInAction(() => {
                if (response.status === 200) {
                    this.selectedResult = response.data?.localResult || response.data;
                } else {
                    this.error = response.message || 'Failed to fetch result details';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async searchResults(params = {}) {
        this.loading = true;
        this.error = null;

        try {
            const queryString = new URLSearchParams(params).toString();
            const url = `/api/integrations/idexx/results/search${queryString ? '?' + queryString : ''}`;

            const response = await util.fetchApi(url);

            runInAction(() => {
                if (response.status === 200) {
                    this.results = response.data?.results || response.data || [];
                } else {
                    this.error = response.message || 'Failed to search results';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    getResultPdfUrl(resultId, jwtToken) {
        return `/api/integrations/idexx/results/${resultId}/pdf?jwtToken=${jwtToken}`;
    }

    // ============================================
    // Unlinked Results & Linking
    // ============================================

    @action
    async getUnlinkedResults() {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/idexx/results/unlinked');

            runInAction(() => {
                if (response.status === 200) {
                    this.unlinkedResults = response.data || [];
                } else {
                    this.error = response.message || 'Failed to fetch unlinked results';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async linkResult(resultId, animalId, journalId) {
        this.loading = true;
        this.error = null;

        try {
            const body = { resultId, animalId };
            if (journalId) {
                body.journalId = journalId;
            }

            const response = await util.fetchApi('/api/integrations/idexx/results/link', {
                method: 'POST',
            }, body);

            runInAction(() => {
                if (response.status === 200) {
                    // Remove from unlinked list
                    this.unlinkedResults = this.unlinkedResults.filter(r => r.id !== resultId);
                } else {
                    this.error = response.message || 'Failed to link result';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async getResultsForAnimal(animalId) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi(`/api/integrations/idexx/results/search?animal=${animalId}`);

            runInAction(() => {
                if (response.status === 200) {
                    this.animalResults = response.data?.results || response.data || [];
                } else {
                    this.error = response.message || 'Failed to fetch animal results';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    // ============================================
    // Computed Properties
    // ============================================

    @computed
    get hasCredentials() {
        const userStore = require('./user').default;
        return !!(userStore.customer?.featureFlags?.idexx);
    }

    @computed
    get hasPimsId() {
        const userStore = require('./user').default;
        return !!(userStore.customer?.featureFlags?.idexxHasPimsId);
    }

    @computed
    get environment() {
        const userStore = require('./user').default;
        return userStore.customer?.featureFlags?.idexxEnvironment || 'production';
    }

    @computed
    get pendingResults() {
        return this.results.filter(r => r.status === 'INPROCESS' || !r.viewedDate);
    }

    @computed
    get abnormalResults() {
        return this.results.filter(r => r.isAbnormal || r.isCritical);
    }

    @computed
    get pendingOrders() {
        return this.orders.filter(o => o.status === 'pending' || o.status === 'submitted' || o.status === 'processing');
    }

    // ============================================
    // Legacy methods (for backwards compatibility)
    // ============================================

    @action
    async submitTest(testData) {
        return this.createOrder(testData);
    }

    @action
    async getResults(params = {}) {
        return this.searchResults(params);
    }
}

const store = new IdexxStore();
export default store;
